import { useState, useEffect } from 'react'
import ReaderItem from './ReaderItem'
import { ReaderResponse } from '../models/ReaderResponse'
import { useQuery } from 'react-query'
import { extractFirmwaresMap, getReaders } from 'api/reader-api'
import { toast } from 'react-toastify'
import { useAppDispatch, useAppSelector } from 'hooks/redux-hooks'
import { setMaxDeltaLast, setSearchValue } from 'store/slices/filterSlice'
import { getDeltaDays, getUtcNow } from 'utils/utils'

const filterReaderResponse = (
  reader: ReaderResponse,
  search: string,
  maxDeltaLast?: number
): boolean => {
  if (maxDeltaLast && reader.last_connection_time) {
    const delta = getDeltaDays(
      getUtcNow(),
      new Date(reader.last_connection_time)
    )
    if (delta > maxDeltaLast) {
      return false
    }
  }

  if (search.length === 0) {
    return true
  }

  const lowerSearchValue = search.toLowerCase()
  if (reader.serial.toString().includes(lowerSearchValue)) {
    return true
  }

  if (reader.note?.toLowerCase().includes(lowerSearchValue)) {
    return true
  }

  if (reader.profile?.name?.toLowerCase().includes(lowerSearchValue)) {
    return true
  }

  if (reader.operator?.name?.toLowerCase().includes(lowerSearchValue)) {
    return true
  }

  const firmwares = extractFirmwaresMap(reader)
  for (let firmware of Array.from(firmwares.values())) {
    if (
      firmware.profile_version?.includes(lowerSearchValue) ||
      firmware.reader_version?.includes(lowerSearchValue)
    ) {
      return true
    }
  }

  return false
}

const ReaderList = () => {
  const {
    isError,
    data: readers,
    error,
  } = useQuery({
    queryKey: ['readers'],
    queryFn: () => getReaders(),
  })

  const dispatch = useAppDispatch()
  const { searchValue, maxDeltaLast } = useAppSelector((state) => state.filter)
  const [filteredData, setFilteredData] = useState<ReaderResponse[]>([])

  useEffect(() => {
    if (!readers) {
      setFilteredData([])
    } else {
      setFilteredData(
        readers.filter((item) =>
          filterReaderResponse(item, searchValue, maxDeltaLast)
        )
      )
    }
  }, [readers, searchValue, maxDeltaLast])

  useEffect(() => {
    if (isError && error instanceof Error) {
      toast.error(error.message)
    }
  }, [isError, error])

  return (
    <div className='w-full m-2 text-base'>
      <div className='flex flex-row'>
        <div className='pb-4 mx-2 bg-white'>
          <label htmlFor='table-search' className='pt-2 mr-2'>
            Search
          </label>
          <input
            type='text'
            id='table-search'
            className='p-2 text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500'
            placeholder='Search for readers'
            value={searchValue}
            onChange={(e) => dispatch(setSearchValue(e.target.value ?? ''))}
            onKeyDown={(e) => {
              if (e.key === 'Escape') {
                dispatch(setSearchValue(''))
              }
            }}
          />
        </div>
        <div className='pb-4 mx-2 bg-white'>
          <label htmlFor='max-delta-last-days' className='pt-2 mr-2'>
            Max Delta Last Days
          </label>
          <input
            type='number'
            min='0'
            step='1'
            id='max-delta-last-days'
            className='p-2 text-gray-900 border border-gray-300 rounded-lg w-80 bg-gray-50 focus:ring-blue-500 focus:border-blue-500'
            value={maxDeltaLast || ''}
            onChange={(e) =>
              dispatch(setMaxDeltaLast(parseInt(e.target.value) ?? 10))
            }
          />
        </div>
      </div>
      <div>
        <table className='w-full table-auto font-sans	text-left rtl:text-right text-gray-500'>
          <thead className='text-gray-700 bg-gray-100'>
            <tr>
              <th scope='col' className='px-6 py-3 border text-center'>
                Serial
              </th>
              <th scope='col' className='px-6 py-3 border text-center'>
                Actions
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Note
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Delta Last
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Mqtt uptime
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Errors
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Firmwares
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Profile
              </th>
              <th scope='col' className='px-6 py-3 border'>
                Operator
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredData?.map((item) => {
              return <ReaderItem reader={item} key={item.id} />
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default ReaderList
